import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Building, 
  Phone, 
  Briefcase, 
  CheckCircle,
  Circle,
  TrendingUp
} from 'lucide-react';

const ProfileCompletionProgress = ({ user }) => {
  // Calculate profile completion
  const getProfileCompletion = () => {
    const fields = [
      { key: 'jobTitle', label: 'Job Title', icon: User, required: true },
      { key: 'companyName', label: 'Company Name', icon: Building, required: true },
      { key: 'phoneNumber', label: 'Phone Number', icon: Phone, required: false },
      { key: 'useCase', label: 'Use Case', icon: Briefcase, required: true },
    ];

    const preferences = [
      { key: 'emailNotifications', label: 'Email Notifications', required: true },
      { key: 'signatureStyle', label: 'Signature Style', required: true },
      { key: 'documentSharing', label: 'Document Sharing', required: true },
      { key: 'timeZone', label: 'Time Zone', required: true },
    ];

    const profileFields = fields.map(field => ({
      ...field,
      completed: user[field.key] ? true : false,
      value: user[field.key] || null
    }));

    const preferenceFields = preferences.map(pref => ({
      ...pref,
      completed: user.userPreferences && user.userPreferences[pref.key] ? true : false,
      value: user.userPreferences ? user.userPreferences[pref.key] : null
    }));

    const allFields = [...profileFields, ...preferenceFields];
    const requiredFields = allFields.filter(field => field.required);
    const completedRequired = requiredFields.filter(field => field.completed);
    const completedOptional = allFields.filter(field => !field.required && field.completed);

    const requiredPercentage = (completedRequired.length / requiredFields.length) * 80; // 80% for required
    const optionalPercentage = (completedOptional.length / (allFields.length - requiredFields.length)) * 20; // 20% for optional
    const totalPercentage = Math.round(requiredPercentage + optionalPercentage);

    return {
      totalPercentage,
      completedRequired: completedRequired.length,
      totalRequired: requiredFields.length,
      completedOptional: completedOptional.length,
      totalOptional: allFields.length - requiredFields.length,
      profileFields,
      preferenceFields,
      allFields
    };
  };

  const completion = getProfileCompletion();

  // Apple-inspired colors matching the design system
  const colors = {
    accent: '#1e40af',
    accentHover: '#1e3a8a',
    success: 'rgb(52, 199, 89)',
    warning: 'rgb(255, 149, 0)',
    gray50: '#f9f9f9',
    gray100: '#f3f3f3',
    gray200: '#e6e6e6',
    gray300: '#d1d1d1',
    gray500: '#8e8e8e',
    gray700: '#636363',
    gray900: '#333333',
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return colors.success;
    if (percentage >= 60) return colors.warning;
    return colors.accent;
  };

  const getCompletionStatus = (percentage) => {
    if (percentage >= 100) return 'Complete';
    if (percentage >= 80) return 'Almost Complete';
    if (percentage >= 60) return 'Good Progress';
    if (percentage >= 40) return 'Getting Started';
    return 'Just Started';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: `${getCompletionColor(completion.totalPercentage)}15`,
              color: getCompletionColor(completion.totalPercentage)
            }}
          >
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Profile Completion
            </h3>
            <p className="text-sm text-gray-500">
              {getCompletionStatus(completion.totalPercentage)}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div 
            className="text-2xl font-bold"
            style={{ color: getCompletionColor(completion.totalPercentage) }}
          >
            {completion.totalPercentage}%
          </div>
          <div className="text-xs text-gray-500">
            {completion.completedRequired}/{completion.totalRequired} required
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div 
          className="w-full rounded-full h-3 mb-2"
          style={{ backgroundColor: colors.gray200 }}
        >
          <motion.div
            className="h-3 rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: `${completion.totalPercentage}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            style={{
              backgroundColor: getCompletionColor(completion.totalPercentage),
              boxShadow: `0 2px 8px ${getCompletionColor(completion.totalPercentage)}30`
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0%</span>
          <span>{completion.totalPercentage}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Profile Fields */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <User className="w-4 h-4 mr-2" />
            Profile Information
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {completion.profileFields.map((field) => {
              const Icon = field.icon;
              return (
                <div
                  key={field.key}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                    field.completed 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className={`flex-shrink-0 ${field.completed ? 'text-green-600' : 'text-gray-400'}`}>
                    {field.completed ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Icon className="w-3 h-3 text-gray-500" />
                      <span className={`text-sm font-medium ${field.completed ? 'text-green-800' : 'text-gray-600'}`}>
                        {field.label}
                      </span>
                      {field.required && (
                        <span className="text-xs text-red-500">*</span>
                      )}
                    </div>
                    {field.completed && field.value && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {field.value}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preferences */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2" />
            Preferences
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {completion.preferenceFields.map((field) => (
              <div
                key={field.key}
                className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                  field.completed 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className={`flex-shrink-0 ${field.completed ? 'text-green-600' : 'text-gray-400'}`}>
                  {field.completed ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium ${field.completed ? 'text-green-800' : 'text-gray-600'}`}>
                    {field.label}
                  </span>
                  {field.completed && field.value && (
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {typeof field.value === 'boolean' ? (field.value ? 'Enabled' : 'Disabled') : field.value}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      {completion.totalPercentage < 100 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">
                Complete Your Profile
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                {completion.completedRequired < completion.totalRequired ? 
                  `Complete ${completion.totalRequired - completion.completedRequired} more required field${completion.totalRequired - completion.completedRequired === 1 ? '' : 's'} to improve your experience.` :
                  'Add optional information to get the most out of SignPact.'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCompletionProgress;